import React from 'react'

import invariant from 'invariant'
import {ConnectedProps} from 'react-redux'
import {Navigate, Route, Routes} from 'react-router'
import {useLocation, useMatch} from 'react-router-dom'
import {replace} from 'redux-first-history'

import * as navigationActions from '../../actions/navigation'
import {State} from '../../reducers'

import useGuard from '../../../../commons/src/hooks/use-guard'
import {connect} from '../../../../commons/src/services/loop'

import {EditProviderInfoTab, ProviderInfo} from '../../types'

import chevronLeftIcon from '../../../../commons/src/assets/icons/chevron-left.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import LinkButton from '../../../../commons/src/components/link-button'
import Tabs from '../../../../commons/src/components/tabs'
import Tab from '../../../../commons/src/components/tabs/Tab'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'
import Container from '../../components/container'
import PageContentLayout from '../../components/page-content-layout'
import PageHeader from '../../components/page-content-layout/PageHeader'

import ROUTES from '../../routes'
import PagePartial from '../page-partial'

import EditDimensionsPartial from './edit-dimensions-partial'
import EditDynamicProductionTimePartial from './edit-dynamic-production-time-partial'
import EditMaterialNamePartial from './edit-material-name-partial'
import EditPricingPartial from './edit-pricing-partial'
import EditPrintingMethodPartial from './edit-printing-method-partial'
import EditProductionTimesPartial from './edit-production-times-partial'

const mapStateToProps = (state: State) => ({
  provider: state.core.provider,
  materials: state.core.materials,
  materialConfigs: state.core.materialConfigs,
  finishGroups: state.core.finishGroups,
  colors: state.core.colors,
})

const mapDispatchToProps = {
  goToProviderInfos: navigationActions.goToProviderInfos,
  goToEditProviderInfo: navigationActions.goToEditProviderInfo,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type Props = ConnectedProps<typeof connector>

const EditProviderInfoPage = ({
  provider,
  materials,
  materialConfigs,
  finishGroups,
  colors,
  goToProviderInfos,
  goToEditProviderInfo,
}: Props) => {
  invariant(provider, 'Provider missing.')

  const routerLocation = useLocation()
  const locationState = routerLocation.state as undefined | {providerInfos: ProviderInfo[]}

  const match = useMatch<'tab', string>({
    path: `${ROUTES.EDIT_PROVIDER_INFO}/:tab`,
  })

  const providerInfos = locationState?.providerInfos || []
  const activeTab = match?.params.tab || EditProviderInfoTab.PRINTING_METHOD

  useGuard(providerInfos.length > 0, goToProviderInfos)

  const renderConfigurationTooltip = () => (
    <TooltipHandler
      tooltip={
        <Tooltip>
          {providerInfos.map((providerInfo, index) => {
            const materialConfig = materialConfigs[providerInfo.materialConfigId]
            const finishGroup = finishGroups[materialConfig.finishGroupId]
            const color = colors[materialConfig.colorId]

            return (
              <div key={index}>
                <strong>{materials[finishGroup.materialId].name}</strong>, {finishGroup.name},{' '}
                {color.name}
              </div>
            )
          })}
        </Tooltip>
      }
    >
      <LinkButton>
        {providerInfos.length} configuration{providerInfos.length > 1 ? 's' : ''}
      </LinkButton>
    </TooltipHandler>
  )

  return (
    <PagePartial>
      <PageContentLayout
        header={
          <PageHeader
            headline={
              <ActionBar>
                <Button
                  hierarchy="secondary"
                  icon={<Icon source={chevronLeftIcon} />}
                  onClick={() => goToProviderInfos()}
                />
                <Headline
                  size="l"
                  label="Edit details"
                  addendum={renderConfigurationTooltip()}
                  classNames={['u-no-margin-bottom']}
                />
              </ActionBar>
            }
          >
            <Tabs
              activeTab={activeTab}
              onChange={tabKey =>
                goToEditProviderInfo(providerInfos, tabKey as EditProviderInfoTab, replace)
              }
            >
              <Tab label="Name" tabKey={EditProviderInfoTab.MATERIAL_NAME} />
              <Tab
                label="Printing method &amp; infills"
                tabKey={EditProviderInfoTab.PRINTING_METHOD}
              />
              <Tab label="Dimensions" tabKey={EditProviderInfoTab.DIMENSIONS} />
              <Tab label="Production time" tabKey={EditProviderInfoTab.PRODUCTION_TIME} />
              <Tab
                label="Dynamic production time"
                tabKey={EditProviderInfoTab.DYNAMIC_PRODUCTION_TIME}
              />
              <Tab label="Pricing" tabKey={EditProviderInfoTab.PRICING} />
            </Tabs>
          </PageHeader>
        }
      >
        <Container size="s">
          {providerInfos.length === 0 && <Navigate to={ROUTES.PROVIDER_INFOS} />}
          <Routes>
            <Route
              path={EditProviderInfoTab.MATERIAL_NAME}
              element={<EditMaterialNamePartial providerInfos={providerInfos} />}
            />
            <Route
              path={EditProviderInfoTab.PRINTING_METHOD}
              element={
                <EditPrintingMethodPartial providerId={provider.id} providerInfos={providerInfos} />
              }
            />
            <Route
              path={EditProviderInfoTab.DIMENSIONS}
              element={<EditDimensionsPartial providerInfos={providerInfos} />}
            />
            <Route
              path={EditProviderInfoTab.PRODUCTION_TIME}
              element={
                <EditProductionTimesPartial
                  providerId={provider.id}
                  providerInfos={providerInfos}
                />
              }
            />
            <Route
              path={EditProviderInfoTab.DYNAMIC_PRODUCTION_TIME}
              element={
                <EditDynamicProductionTimePartial
                  providerId={provider.id}
                  providerInfos={providerInfos}
                />
              }
            />
            <Route
              path={EditProviderInfoTab.PRICING}
              element={
                <EditPricingPartial providerId={provider.id} providerInfos={providerInfos} />
              }
            />
          </Routes>
        </Container>
      </PageContentLayout>
    </PagePartial>
  )
}

export default connector(EditProviderInfoPage)
